// =====================================================================================
// FILE: src/hooks/useDataLoader.js (FIXED FOR DYNAMIC CATEGORIES + SAVE COOLDOWN)
// =====================================================================================
import { useState, useEffect, useCallback, useRef } from "react";
import { canonicalCategory, makeAllowedSet, cleanMulti, norm } from "../utils/categoryUtils";
import { POLL_MS } from "../constants/views";

// Cooldown period after saves — prevents polling from overwriting fresh changes
// with stale Airtable data (eventual consistency window)
const SAVE_COOLDOWN_MS = 15000; // 15 seconds — covers 2+ poll cycles for Airtable propagation

// Pending saves: track which fields were just saved so polling can't overwrite them
// Map<recordId, { fields: { fieldName: value }, savedAt: timestamp }>
const PENDING_FIELD_PROTECTION_MS = 20000; // 20 seconds — protect individual fields longer than general cooldown

export function useDataLoader(
  api,
  fixtureKey,
  viewName,
  categoryOrder,
  warmAssetCache,
  isInGame,
  isLed,
  isTeam,
  isIptv
) {
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("Loading…");
  const isDraggingRef = useRef(false);
  const isSavingRef = useRef(false);
  const [showModal, setShowModal] = useState(false);
  const [editingCell, setEditingCell] = useState(null);
  
  // ✅ FIXED: Use refs to track modal/editing state for polling checks
  // This prevents stale closures in the interval callback
  const showModalRef = useRef(false);
  const editingCellRef = useRef(null);
  
  // ✅ NEW: Save cooldown — tracks when the last save completed
  // Polling is paused for SAVE_COOLDOWN_MS after any save to let Airtable propagate
  const lastSaveTimeRef = useRef(0);
  const wasSavingRef = useRef(false); // tracks previous isSavingRef state for edge detection
  
  // ✅ NEW: Pending field protection — remembers recently saved values
  // so polling merges them back if Airtable returns stale data
  const pendingFieldsRef = useRef(new Map());
  
  // Keep refs in sync with state
  useEffect(() => { showModalRef.current = showModal; }, [showModal]);
  useEffect(() => { editingCellRef.current = editingCell; }, [editingCell]);
  
  // ✅ FIXED: Check if we're using dynamic categories (empty array)
  const useDynamicCategories = !categoryOrder || categoryOrder.length === 0;
  const allowedCategories = useDynamicCategories ? null : makeAllowedSet(categoryOrder);
  const normalizeAndFilter = useCallback(
    (data) => {
      const normalized = (data || []).map((it) => {
        // Only try to canonicalize if we have a category order
        const cat = useDynamicCategories 
          ? String(it.category || "").trim()
          : canonicalCategory(it.category, categoryOrder);
        
        const fixed = cat || it.category || "";
        
        return {
          ...it,
          category: fixed,
          selectTiming: cleanMulti(it.selectTiming),
          selectLocation: cleanMulti(it.selectLocation),
          rotations: Number(it.rotations || 0),
        };
      });
      // ✅ FIXED: Only filter if we have allowed categories
      // If using dynamic categories, return all items
      if (useDynamicCategories) {
        return normalized;
      }
      return normalized.filter((it) => allowedCategories.has(norm(it.category)));
    },
    [categoryOrder, allowedCategories, useDynamicCategories]
  );
  const load = useCallback(
    async ({ silent } = { silent: false }) => {
      try {
        if (!silent) setStatus("Loading…");
        const opts = viewName ? { viewName } : {};
        const data = await api.fetch(fixtureKey, opts);
        let normalized = normalizeAndFilter(data);
        
        // ✅ Merge pending field protection: overwrite stale poll data with recently saved values
        const now = Date.now();
        const pending = pendingFieldsRef.current;
        if (pending.size > 0) {
          const expiredIds = [];
          normalized = normalized.map(item => {
            const saved = pending.get(item.id);
            if (!saved) return item;
            if (now - saved.savedAt > PENDING_FIELD_PROTECTION_MS) {
              expiredIds.push(item.id);
              return item; // Protection expired, use server data
            }
            // Merge: saved values override stale poll values
            return { ...item, ...saved.fields };
          });
          // Cleanup expired entries
          expiredIds.forEach(id => pending.delete(id));
        }
        
        setItems(normalized);
        setStatus("");
        warmAssetCache(normalized);
      } catch (e) {
        console.error("useDataLoader: Error loading data", e);
        setStatus(String(e?.message || e));
      }
    },
    [api, fixtureKey, viewName, normalizeAndFilter, warmAssetCache]
  );

  // ✅ NEW: Verified save — wraps api.update with re-read confirmation
  // Call this instead of raw api.update for critical content assignment saves
  const verifiedUpdate = useCallback(
    async (recordId, payload) => {
      await api.update(recordId, payload);
      
      // Re-read after a short delay to verify the write stuck
      await new Promise(r => setTimeout(r, 500));
      try {
        const opts = viewName ? { viewName } : {};
        const freshData = await api.fetch(fixtureKey, opts);
        const freshRecord = freshData.find(r => r.id === recordId);
        if (freshRecord) {
          // Check key fields were written — look for content assignment fields
          const contentFields = ['bigGfxVideosId', 'cornerGfxVideosId', 'ledAllTypesId', 'mcgTiledLedId', 'mcg360Id', 'iptvGfxId', 'superFullId', 'super169Id', 'superSideId', 'marvelTiledLedId', 'marvel360Id', 'marvelInterchangeId'];
          for (const field of contentFields) {
            if (payload[field] !== undefined && payload[field] && !freshRecord[field]) {
              console.warn(`[useDataLoader] ⚠ Verify failed: ${field} was set to "${payload[field]}" but read back as empty — retrying`);
              await api.update(recordId, { [field]: payload[field] });
              await new Promise(r => setTimeout(r, 300));
            }
          }
        }
      } catch (verifyErr) {
        console.warn("[useDataLoader] Verify re-read failed (non-fatal):", verifyErr.message);
      }
    },
    [api, fixtureKey, viewName]
  );

  useEffect(() => {
    load();
    const t = setInterval(() => {
      // ✅ Detect save completion edge: isSavingRef was true, now false → start cooldown
      if (wasSavingRef.current && !isSavingRef.current) {
        lastSaveTimeRef.current = Date.now();
        wasSavingRef.current = false;
        console.log(`⏱️ Save completed — polling paused for ${SAVE_COOLDOWN_MS / 1000}s cooldown`);
      }
      wasSavingRef.current = isSavingRef.current;

      // ✅ FIXED: Use refs for all checks to avoid stale closures
      if (isDraggingRef.current || isSavingRef.current || showModalRef.current || editingCellRef.current) {
        return;
      }

      // ✅ NEW: Check save cooldown — don't poll if a save just completed
      const timeSinceSave = Date.now() - lastSaveTimeRef.current;
      if (timeSinceSave < SAVE_COOLDOWN_MS) {
        const remaining = Math.round((SAVE_COOLDOWN_MS - timeSinceSave) / 1000);
        console.log(`⏱️ Polling paused — save cooldown (${remaining}s remaining)`);
        return;
      }

      console.log("🔄 Polling - loading data silently");
      load({ silent: true });
    }, POLL_MS);
    return () => clearInterval(t);
    // ✅ FIXED: Don't include showModal/editingCell as dependencies
    // They should only be checked inside the interval via refs
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fixtureKey, viewName, isInGame, isLed, isTeam, isIptv]);
  // ✅ Register a save for field protection — called by App.js on every save
  const registerPendingSave = useCallback((recordId, fields) => {
    pendingFieldsRef.current.set(recordId, { fields, savedAt: Date.now() });
  }, []);

  return {
    items,
    setItems,
    status,
    load,
    isDraggingRef,
    isSavingRef,
    showModal,
    setShowModal,
    editingCell,
    setEditingCell,
    verifiedUpdate,
    registerPendingSave,
  };
}
